import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import { getLineItemNotReturnedQuantity } from "@babackapp/calculations/get-line-item-not-returned-quantity";
import { getLineItemNotTreatedByLogisticianQuantity } from "@babackapp/calculations/get-line-item-not-treated-by-logistician-quantity";
import { useCanLogisticianRevalidateAfterMerchant } from "../../hooks/use-can-logistician-revalidate-after-merchant";
import { getLineItemRejectedByLogisticianQuantity } from "@babackapp/calculations/get-line-item-rejected-by-logistician-quantity";
import {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from "react";
import { useCurrentReturnRequest } from "../../utils/current-return-request-context";
import { getLineItemReadyToBeTreatedByMerchantSelectableQuantityWithWorkflowLogistician } from "../../utils/get-line-item-ready-to-be-treated-by-merchant-selectable-quantity-with-workflow-logistician";
import { getLineItemReadyToBeTreatedByMerchantSelectableQuantityWithoutWorkflowLogistician } from "../../utils/get-line-item-ready-to-be-treated-by-merchant-selectable-quantity-without-workflow-logistician";
import { getLineItemRejectedByMerchantSelectableQuantity } from "../../utils/get-line-item-rejected-by-merchant-selectable-quantity";
import {
  ReturnRequestLineItemDto,
  ShopDto,
} from "@babackapp/private-api-client/types";
import { getLineItemQuantityAlreadyValidatedByMerchant } from "@/pages/(app)/returns/utils/get-line-item-quantity-already-validated-by-merchant.ts";
import { getLineItemNotTreatedByMerchantQuantity } from "@babackapp/calculations/get-line-item-not-treated-by-merchant-quantity";

// Example data structure
// {
//   "123": {
//     "validated": { selectedQuantity: 1, maxQuantity: 1 },
//     "rejected-by-logistician": { selectedQuantity: 0, maxQuantity: 1 },
//     "not-treated-by-logistician": { selectedQuantity: 0, maxQuantity: 1 },
//     "not-returned": { selectedQuantity: 0, maxQuantity: 1 }
//   }
// }

export type RejectLineItemsKey =
  | "total"
  /**
   * Missleading but validated is actually "validated by logistician"
   */
  | "validated-by-merchant"
  | "validated-by-logistician"
  | "rejected-by-logistician"
  | "rejected-by-merchant"
  | "not-treated-by-logistician"
  | "not-treated-by-merchant"
  | "not-returned";

type QuantityState = {
  selectedQuantity: number;
  maxQuantity: number;
};

type LineItemState = Record<RejectLineItemsKey, QuantityState>;

type LineItemsState = Record<string, LineItemState>;

type LineItemStateUpdater = (
  id: string,
  state: RejectLineItemsKey,
  quantity: number,
  reason?: string,
) => void;

type RejectLineItemsStateContextType = {
  lineItemsState: LineItemsState;
  setLineItemState: LineItemStateUpdater;
};

const RejectLineItemsStateContext =
  createContext<RejectLineItemsStateContextType>({
    lineItemsState: {},
    setLineItemState: () => {},
  });

/**
 * Usage:
 * const { setLineItemState } = useLineItemsState();
 * setLineItemState("id", "validated", 2);
 */
export default function useRejectLineItemsState() {
  return useContext(RejectLineItemsStateContext);
}

/**
 * This provider will keep track of the state of each line item we want to validate.
 * By default, they are all valid. By setting the quantity manually, or changing their state
 * in the modal, we can update this state.
 */
export function RejectLineItemsStateProvider({ children }: PropsWithChildren) {
  const currentReturnRequest = useCurrentReturnRequest();
  const { data: shop } = useCurrentShopSuspense();
  const canRevalidateAfterMerchant = useCanLogisticianRevalidateAfterMerchant();

  const lineItems = currentReturnRequest.line_items;
  //const lineItems = removeLineItemsAcceptedByLogistician(
  //  currentReturnRequest.line_items,
  //);

  /**
   * We assign a default state to each line item. For example,
   * if a user wants to return 2 white t-shirts, one of them might be rejected,
   * and the other validated.
   */
  const [lineItemsState, _setLineItemsState] = useState(() =>
    getLineItemsInitialState(shop, lineItems, canRevalidateAfterMerchant),
  );

  /**
   * When the return request changes, we need to update the state of the line items.
   * (Resetting them back to their initial state)
   */
  useEffect(() => {
    const lineItems = currentReturnRequest.line_items;
    //const lineItems = removeLineItemsAcceptedByLogistician(
    //  currentReturnRequest.line_items,
    //);
    _setLineItemsState(
      getLineItemsInitialState(shop, lineItems, canRevalidateAfterMerchant),
    );
  }, [currentReturnRequest, shop, canRevalidateAfterMerchant]);

  /**
   * To update the state of a line item, we need to provide the line item id,
   * the state we want to update, and the quantity we want to set.
   * @param id
   * @param state
   * @param quantity
   * @returns
   */
  const setLineItemState: LineItemStateUpdater = (id, state, quantity) => {
    return _setLineItemsState((prev) => {
      return {
        ...prev,
        [id]: {
          ...prev[id],
          [state]: {
            ...prev[id][state],
            selectedQuantity: quantity,
          },
          get total() {
            return {
              selectedQuantity:
                this["validated-by-merchant"].selectedQuantity +
                this["validated-by-logistician"].selectedQuantity +
                this["rejected-by-logistician"].selectedQuantity +
                this["rejected-by-merchant"].selectedQuantity +
                this["not-treated-by-logistician"].selectedQuantity +
                this["not-treated-by-merchant"].selectedQuantity +
                this["not-returned"].selectedQuantity,
              maxQuantity: 0,
            };
          },
        },
      };
    });
  };

  return (
    <RejectLineItemsStateContext.Provider
      value={{ lineItemsState, setLineItemState }}
    >
      {children}
    </RejectLineItemsStateContext.Provider>
  );
}

function valueSelector(state: RejectLineItemsKey) {
  return function selectorHook(lineItemId: string) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useRejectLineItemsState().lineItemsState[lineItemId][state];
  };
}

function setterSelector(state: RejectLineItemsKey) {
  return function selectorSetterHook(lineItemId: string) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const lineItemState = useRejectLineItemsState();
    return (quantity: number) =>
      lineItemState.setLineItemState(lineItemId, state, quantity);
  };
}

export const useValidatedByLogistician = valueSelector(
  "validated-by-logistician",
);
export const useSetValidatedByLogistician = setterSelector(
  "validated-by-logistician",
);

export const useNotTreatedByLogistician = valueSelector(
  "not-treated-by-logistician",
);
export const useSetNotTreatedByLogistician = setterSelector(
  "not-treated-by-logistician",
);

export const useRejectedByLogistician = valueSelector(
  "rejected-by-logistician",
);
export const useSetRejectedByLogistician = setterSelector(
  "rejected-by-logistician",
);

export const useNotTreatedByMerchant = valueSelector("not-treated-by-merchant");
export const useSetNotTreatedByMerchant = setterSelector(
  "not-treated-by-merchant",
);

export const useRejectedByMerchant = valueSelector("rejected-by-merchant");
export const useSetRejectedByMerchant = setterSelector("rejected-by-merchant");

export const useNotReturned = valueSelector("not-returned");
export const useSetNotReturned = setterSelector("not-returned");

function getLineItemsInitialState(
  shop: ShopDto,
  lineItems: ReturnRequestLineItemDto[],
  canRevalidateAfterMerchant = false,
) {
  return lineItems.reduce((acc, lineItem) => {
    const maxQuantityValidated = shop.workflowLogistician.activated
      ? getLineItemQuantityAlreadyValidatedByMerchant({
          lineItem,
        })
      : getLineItemReadyToBeTreatedByMerchantSelectableQuantityWithoutWorkflowLogistician(
          { lineItem },
        );

    const maxQuantityValidatedByLogistician = shop.workflowLogistician.activated
      ? getLineItemReadyToBeTreatedByMerchantSelectableQuantityWithWorkflowLogistician(
          { lineItem },
        )
      : 0;

    const maxQuantityReadyToBeTreatedByLogistician = shop.workflowLogistician
      .activated
      ? getLineItemNotTreatedByLogisticianQuantity({
          lineItem,
          allowRevalidationAfterMerchant: canRevalidateAfterMerchant,
        })
      : 0;

    const maxQuantityReadyToBeTreatedByMerchant = shop.workflowLogistician
      .activated
      ? 0
      : getLineItemNotTreatedByMerchantQuantity({ lineItem });
    const maxQuantityRejectedByMerchant =
      getLineItemRejectedByMerchantSelectableQuantity({
        lineItem,
      });

    const maxQuantityRejectedByLogistician =
      getLineItemRejectedByLogisticianQuantity({
        lineItem,
      });

    const maxQuantityNotReturned = getLineItemNotReturnedQuantity({
      lineItem,
    });

    return {
      ...acc,
      [lineItem.line_item_id]: {
        "validated-by-merchant": {
          selectedQuantity: 0,
          maxQuantity: maxQuantityValidated,
        },
        "validated-by-logistician": {
          selectedQuantity: 0,
          maxQuantity: maxQuantityValidatedByLogistician,
        },
        "rejected-by-logistician": {
          selectedQuantity: 0,
          maxQuantity: maxQuantityRejectedByLogistician,
        },
        "rejected-by-merchant": {
          selectedQuantity: 0,
          maxQuantity: maxQuantityRejectedByMerchant,
        },
        "not-treated-by-logistician": {
          selectedQuantity: 0,
          maxQuantity: maxQuantityReadyToBeTreatedByLogistician,
        },
        "not-treated-by-merchant": {
          selectedQuantity: 0,
          maxQuantity: maxQuantityReadyToBeTreatedByMerchant,
        },
        "not-returned": {
          selectedQuantity: 0,
          maxQuantity: maxQuantityNotReturned,
        },
        get total() {
          return {
            selectedQuantity:
              this["validated-by-merchant"].selectedQuantity +
              this["validated-by-logistician"].selectedQuantity +
              this["rejected-by-logistician"].selectedQuantity +
              this["rejected-by-merchant"].selectedQuantity +
              this["not-treated-by-logistician"].selectedQuantity +
              this["not-treated-by-merchant"].selectedQuantity +
              this["not-returned"].selectedQuantity,
            maxQuantity: 0,
          };
        },
      },
    };
  }, {} as LineItemsState);
}
